import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

const Styled = styled(Box, {
  shouldForwardProp: (prop) => prop !== "severity",
})(({ theme, severity = "warning" }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "100vw",
  margin: "0 auto",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  zIndex: 1100,
  [theme.breakpoints.down("sm")]: {
    width: "94%",
  },
  "& .MuiAlert-root": {
    borderRadius: theme.spacing(1),
    border: `1px solid ${
      (theme.palette[severity] && theme.palette[severity].dark) ||
      theme.palette.warning.dark
    }`,
    boxShadow: theme.shadows[3],
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    "& .MuiAlert-message": {
      width: "100%",
      whiteSpace: "pre-line",
      lineHeight: 1.5,
    },
  },
  ...theme.applyStyles("dark", { backgroundColor: undefined }),
}));

const ALLOWED_SEVERITIES = ["info", "warning", "error", "success"];
const DISMISSED_KEY = "downtimeBanner.dismissed";

const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch (_) {
    return null;
  }
};
const writeDismissed = (value) => {
  try {
    if (value) localStorage.setItem(DISMISSED_KEY, value);
    else localStorage.removeItem(DISMISSED_KEY);
  } catch (_) {
    /* quota / private mode — nothing else to do */
  }
};

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

function DowntimeBanner({ isLoggedIn = false }) {
  const location = useLocation();
  const { isMaintenance } = useSelector((state) => state.maintenance);
  const [data, setData] = useState(null);
  const [lastDismissed] = useState(readDismissed);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/downtime", { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch(() => {
        /* the banner is non-critical: stay silent and render nothing */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const downtime = data?.downtime;
  const message = downtime?.message;

  const dismissKey = data?.version;
  const hasEnded = downtime?.end && new Date(downtime.end) <= new Date();
  const isBrowserSupportPage = location.pathname.includes("browser-support");
  const severity = ALLOWED_SEVERITIES.includes(downtime?.severity)
    ? downtime.severity
    : "warning";

  const isDismissed = closed || (dismissKey && dismissKey === lastDismissed);

  const dismiss = () => {
    setClosed(true);
    writeDismissed(dismissKey);
  };

  if (isBrowserSupportPage || isMaintenance) return null;
  if (!message || hasEnded || isDismissed) return null;

  const window =
    downtime.start && downtime.end
      ? `${fmt(downtime.start)} — ${fmt(downtime.end)}`
      : null;

  return (
    <Styled
      sx={{ position: !isLoggedIn ? "fixed" : "relative" }}
      severity={severity}
    >
      <Alert
        sx={{ width: "80%", mx: "auto" }}
        severity={severity}
        role="alert"
        onClose={dismiss}
      >
        <AlertTitle>{downtime.title || "Scheduled maintenance"}</AlertTitle>
        <Typography component="span" variant="body2">
          {message}
        </Typography>
        {window && (
          <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
            {window}
          </Typography>
        )}
      </Alert>
    </Styled>
  );
}

export default DowntimeBanner;
